import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash-es';
import { OnboardingWidgetState } from 'anchor-scout-common/types';
import { Button, Callout, FunctionalLoader, Page, Spinner, useOverlayContext, useStateInRef, useDevice } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useStartKYC } from '~/hooks/useStartKYCPersona';
import { useLocaleKeys } from '~/i18n';
import InvoicesDigestWidget from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoicesDigestWidget';
import { selectors } from '~/store/state-selectors';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import BusinessSetupWidget from './BusinessSetupWidget/BusinessSetupWidget';
import HistoricAgreementStatsWidget from './HistoricAgreementStatsWidget/HistoricAgreementStatsWidget';
import { useGetInfo } from './hooks/useGetInfo';
import { useUpdateOnboardingState } from './hooks/useUpdateOnboardingState';
import PayoutsSummaryWidget from './PayoutsSummaryWidget/PayoutsSummaryWidget';
import RevenueWidget from './RevenueWidget/RevenueWidget';
import SalesWidget from './SalesWidget/SalesWidget';
import styles from './VendorDashboard.module.scss';
import { getExternalAccountWarning, getKycWarning, kycWarningTextsPath } from './VendorDashboard.utils';
export function VendorDashboard() {
    const { isMobileDevice } = useDevice();
    const { t } = useLocaleKeys('pages.dashboard');
    const { t: kycWarningTexts } = useLocaleKeys(kycWarningTextsPath);
    const hasBusiness = useStore(selectors.user.hasBusiness);
    const userFirstName = useStore(selectors.user.firstName) || '';
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const initialData = useStore((s)=>s.personaKYBStatus.data);
    const { fetchPersonaKYBStatus } = useStoreActions();
    const { startKYC } = useStartKYC();
    const { logBI } = useStoreActions();
    const { businessId, businessInfo, optimisticUpdate, isLoading } = useGetInfo();
    const { updateOnboardingState } = useUpdateOnboardingState();
    const getStateInRef = useStateInRef({
        businessId,
        businessInfo,
        updateOnboardingState,
        optimisticUpdate
    });
    const { getNow } = useIoContext();
    const { getOpenedModals } = useOverlayContext();
    const coveredByModal = getOpenedModals().length > 0;
    const worksAt = useStore(selectors.user.business);
    const personaKYBStatus = useStore((s)=>s.personaKYBStatus.data);
    const kycWarning = getKycWarning(worksAt, personaKYBStatus != null && personaKYBStatus.timestamp > getNow().getTime() - 1000 * 60 * 5, kycWarningTexts);
    const externalAccountWarning = worksAt ? getExternalAccountWarning(worksAt) : null;
    useSetDocumentTitle(t.pageTitle());
    const debouncedUpdateOnboardingState = useMemo(()=>debounce((expanded)=>{
            const { businessId, businessInfo, updateOnboardingState, optimisticUpdate } = getStateInRef();
            if (businessInfo) {
                const onboardingWidgetState = expanded ? OnboardingWidgetState.StaticOpened : OnboardingWidgetState.Hidden;
                updateOnboardingState({
                    businessId: businessId,
                    onboardingWidgetState
                });
                optimisticUpdate((old)=>_object_spread_props(_object_spread({}, old), {
                        onboardingWidgetState: onboardingWidgetState
                    }));
            }
        }, 10000), []);
    useOnMount(()=>{
        return ()=>{
            debouncedUpdateOnboardingState.flush();
        };
    });
    const setupWidgetViewingState = useMemo(()=>{
        if (coveredByModal && (businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState) === OnboardingWidgetState.ShouldReveal) {
            return null;
        }
        return businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState;
    }, [
        businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState,
        coveredByModal
    ]);
    if (hasBusiness && isLoading) return /*#__PURE__*/ _jsx(FunctionalLoader, {
        show: true
    });
    const renderOnboardingWidget = ()=>setupWidgetViewingState ? /*#__PURE__*/ _jsx(BusinessSetupWidget, {
            viewingState: setupWidgetViewingState,
            onAnimationEnd: debouncedUpdateOnboardingState
        }) : null;
    const renderDashboard = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                kycWarning && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    hideIcon: true,
                    className: styles.kycIncompleteBanner,
                    testId: kycWarning.testId,
                    withAction: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'kyc_warning_start_kyc'
                        },
                        onClick: ()=>{
                            logBI(11203);
                            startKYC();
                        },
                        skin: "darkSecondary",
                        children: t.kycWarning.buttonText()
                    }),
                    children: kycWarning.text
                }),
                externalAccountWarning && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    testId: externalAccountWarning.testId,
                    children: externalAccountWarning.text
                }),
                !isMemberUser ? renderOnboardingWidget() : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: cn(styles.widgets, {
                        [styles.mobile]: isMobileDevice
                    }),
                    "data-testid": "vendor-dashboard-widgets",
                    children: [
                        /*#__PURE__*/ _jsx(InvoicesDigestWidget, {}),
                        /*#__PURE__*/ _jsx(HistoricAgreementStatsWidget, {}),
                        /*#__PURE__*/ _jsx(PayoutsSummaryWidget, {}),
                        /*#__PURE__*/ _jsx(RevenueWidget, {}),
                        /*#__PURE__*/ _jsx(SalesWidget, {})
                    ]
                })
            ]
        });
    return /*#__PURE__*/ _jsx(Page, {
        testId: "vendor-dashboard",
        header: t.greeting({
            firstName: userFirstName
        }),
        actions: /*#__PURE__*/ _jsx(CreateProposalButton, {}),
        minWidth: "1090px",
        children: hasBusiness ? /*#__PURE__*/ _jsx(RestfulWrapper, {
            initialData: initialData,
            fetch: fetchPersonaKYBStatus,
            renderChild: renderDashboard,
            renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                    testId: "vendor-dashboard-loading",
                    centeredInBox: true
                })
        }) : /*#__PURE__*/ _jsx("div", {
            "data-testid": "vendor-dashboard-no-business"
        })
    });
}
